import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getClients, getUsers, getClientActiveBusinessUnits,
  getCustomerFeedbacks, getCustomerFeedback, createCustomerFeedback,
  updateCustomerFeedback, deleteCustomerFeedback
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger
} from '../components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList
} from '../components/ui/command';
import {
  Plus, Loader2, MessageSquare, Calendar, User, Link2, FileText, Trash2,
  Pencil, ChevronRight, ThumbsUp, Minus, ThumbsDown, Building2, Star,
  ChevronsUpDown, Check, Search, BarChart3, List
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../lib/auth';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import FeedbackDashboard from './FeedbackDashboard';

const RATING_VALUES = ['positivo', 'neutro', 'negativo'];
const RATING_COLS = [
  { key: 'processi', label: 'Processi' },
  { key: 'operativi', label: 'Operativi' },
  { key: 'pm', label: 'PM' },
  { key: 'risultati', label: 'Risultati' },
];

function RatingToggle({ value, onChange, disabled }) {
  const options = [
    { val: 'positivo', icon: ThumbsUp, color: 'bg-emerald-500 text-white', ring: 'ring-emerald-300' },
    { val: 'neutro', icon: Minus, color: 'bg-amber-400 text-white', ring: 'ring-amber-200' },
    { val: 'negativo', icon: ThumbsDown, color: 'bg-red-500 text-white', ring: 'ring-red-300' },
  ];
  return (
    <div className="inline-flex gap-1">
      {options.map(opt => {
        const active = value === opt.val;
        const Icon = opt.icon;
        return (
          <button
            key={opt.val}
            type="button"
            disabled={disabled}
            onClick={() => onChange(active ? null : opt.val)}
            className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-150
              ${active ? `${opt.color} ring-2 ${opt.ring} scale-110 shadow-sm` : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            data-testid={`rating-${opt.val}`}
          >
            <Icon className="w-3.5 h-3.5" />
          </button>
        );
      })}
    </div>
  );
}

function NPSSelector({ value, onChange }) {
  return (
    <div className="flex gap-1">
      {[1,2,3,4,5,6,7,8,9,10].map(n => {
        const active = value === n;
        let color = 'bg-gray-100 text-gray-500 hover:bg-gray-200';
        if (active) {
          if (n <= 6) color = 'bg-red-500 text-white ring-2 ring-red-200';
          else if (n <= 8) color = 'bg-amber-400 text-white ring-2 ring-amber-200';
          else color = 'bg-emerald-500 text-white ring-2 ring-emerald-200';
        }
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange(active ? null : n)}
            className={`w-9 h-9 rounded-lg text-sm font-semibold transition-all duration-150 ${color}`}
            data-testid={`nps-${n}`}
          >
            {n}
          </button>
        );
      })}
    </div>
  );
}

function FeedbackForm({ feedback, onSave, onCancel, isNew }) {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [clientId, setClientId] = useState(feedback?.client_id || '');
  const [feedbackDate, setFeedbackDate] = useState(feedback?.feedback_date || new Date().toISOString().split('T')[0]);
  const [responsibleUserId, setResponsibleUserId] = useState(feedback?.responsible_user_id || user?.id || '');
  const [buRatings, setBuRatings] = useState(feedback?.bu_ratings || []);
  const [npsScore, setNpsScore] = useState(feedback?.nps_score || null);
  const [notes, setNotes] = useState(feedback?.notes || '');
  const [callUrl, setCallUrl] = useState(feedback?.call_recording_url || '');
  const [loadingBUs, setLoadingBUs] = useState(false);
  const [saving, setSaving] = useState(false);
  const [noBUs, setNoBUs] = useState(false);
  const [clientOpen, setClientOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    Promise.all([
      getClients().catch(() => ({ data: [] })),
      getUsers().catch(() => ({ data: [] })),
    ]).then(([c, u]) => {
      setClients(c.data);
      setUsers(u.data);
    });
  }, []);

  // Load BUs when client changes
  const loadBUs = useCallback(async (cid) => {
    if (!cid) { setBuRatings([]); setNoBUs(false); return; }
    setLoadingBUs(true);
    try {
      const res = await getClientActiveBusinessUnits(cid);
      const activeBUs = res.data || [];
      if (activeBUs.length === 0) {
        setNoBUs(true);
        setBuRatings([]);
      } else {
        setNoBUs(false);
        // Preserve existing ratings if editing and client hasn't changed
        if (feedback?.client_id === cid && feedback?.bu_ratings?.length > 0) {
          setBuRatings(feedback.bu_ratings);
        } else {
          setBuRatings(activeBUs.map(bu => ({
            business_unit_id: bu.business_unit_id,
            business_unit_name: bu.business_unit_name,
            processi: null, operativi: null, pm: null, risultati: null,
            project_ids: bu.project_ids || [],
            pm_ids: bu.pm_ids || [],
            product_ids: bu.product_ids || [],
          })));
        }
      }
    } catch {
      setNoBUs(true);
      setBuRatings([]);
    } finally { setLoadingBUs(false); }
  }, [feedback]);

  useEffect(() => {
    if (clientId) loadBUs(clientId);
  }, [clientId, loadBUs]);

  const updateRating = (buIdx, field, val) => {
    setBuRatings(prev => prev.map((r, i) => i === buIdx ? { ...r, [field]: val } : r));
  };

  const handleSave = async () => {
    if (!clientId) { toast({ title: 'Seleziona un cliente', variant: 'destructive' }); return; }
    if (buRatings.length === 0) { toast({ title: 'Nessuna Business Unit attiva per questo cliente', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      await onSave({
        client_id: clientId,
        feedback_date: feedbackDate,
        responsible_user_id: responsibleUserId,
        bu_ratings: buRatings,
        nps_score: npsScore,
        notes: notes || null,
        call_recording_url: callUrl || null,
      });
    } catch (error) {
      toast({ title: 'Errore', description: error.response?.data?.detail || 'Salvataggio fallito', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const selectedClient = clients.find(c => c.id === clientId);

  return (
    <div className="space-y-5">
      {/* Header fields: client, date, responsible */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-gray-500">Cliente *</Label>
          <Popover open={clientOpen} onOpenChange={setClientOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={clientOpen}
                className="w-full justify-between font-normal"
                data-testid="select-client-trigger"
              >
                {selectedClient ? selectedClient.company_name : "Cerca cliente..."}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[400px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Cerca cliente per nome..." data-testid="client-search-input" />
                <CommandList>
                  <CommandEmpty>Nessun cliente trovato</CommandEmpty>
                  <CommandGroup>
                    {clients.map(c => (
                      <CommandItem
                        key={c.id}
                        value={c.company_name}
                        onSelect={() => {
                          setClientId(c.id);
                          setClientOpen(false);
                        }}
                        data-testid={`client-option-${c.id}`}
                      >
                        <Check className={`mr-2 h-4 w-4 ${clientId === c.id ? 'opacity-100' : 'opacity-0'}`} />
                        {c.company_name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-gray-500">Data Feedback</Label>
          <Input type="date" value={feedbackDate} onChange={e => setFeedbackDate(e.target.value)} data-testid="feedback-date" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-gray-500">Responsabile</Label>
          <Select value={responsibleUserId} onValueChange={setResponsibleUserId}>
            <SelectTrigger data-testid="select-responsible">
              <SelectValue placeholder="Seleziona responsabile" />
            </SelectTrigger>
            <SelectContent>
              {users.map(u => (
                <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* BU Ratings Table */}
      {loadingBUs ? (
        <div className="flex items-center justify-center py-8 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento servizi attivi...
        </div>
      ) : noBUs ? (
        <div className="rounded-lg border-2 border-dashed border-amber-300 bg-amber-50/50 p-6 text-center">
          <Building2 className="w-8 h-8 text-amber-400 mx-auto mb-2" />
          <p className="text-sm font-medium text-amber-700">Nessun servizio attivo per questo cliente</p>
          <p className="text-xs text-amber-500 mt-1">Verifica che il cliente abbia contratti attivi con prodotti associati</p>
        </div>
      ) : buRatings.length > 0 ? (
        <div className="rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full" data-testid="bu-ratings-table">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 w-[180px]">
                  Business Unit
                </th>
                {RATING_COLS.map(col => (
                  <th key={col.key} className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 py-3">
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {buRatings.map((rating, idx) => (
                <tr key={rating.business_unit_id} className={`border-b border-gray-100 last:border-0 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-orange-500 shrink-0" />
                      <span className="text-sm font-medium text-gray-800">{rating.business_unit_name}</span>
                    </div>
                  </td>
                  {RATING_COLS.map(col => (
                    <td key={col.key} className="text-center px-3 py-3">
                      <RatingToggle
                        value={rating[col.key]}
                        onChange={(val) => updateRating(idx, col.key, val)}
                        disabled={false}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : clientId ? null : (
        <div className="rounded-lg border-2 border-dashed border-gray-200 p-8 text-center text-gray-400">
          <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">Seleziona un cliente per compilare il feedback</p>
        </div>
      )}

      {/* NPS Score */}
      {clientId && buRatings.length > 0 && (
        <div className="space-y-2">
          <Label className="text-xs font-medium text-gray-500">NPS - "Quanto ci consiglieresti da 1 a 10?"</Label>
          <NPSSelector value={npsScore} onChange={setNpsScore} />
          <div className="flex gap-4 text-[10px] text-gray-400 mt-1">
            <span className="text-red-400">1-6 Detrattore</span>
            <span className="text-amber-400">7-8 Passivo</span>
            <span className="text-emerald-400">9-10 Promotore</span>
          </div>
        </div>
      )}

      {/* Notes + Call URL */}
      {clientId && buRatings.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-gray-500">Note</Label>
            <Textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Appunti sulla call..."
              rows={3}
              className="resize-none"
              data-testid="feedback-notes"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-gray-500">Link Registrazione Call</Label>
            <div className="flex items-center gap-2">
              <Link2 className="w-4 h-4 text-gray-400 shrink-0" />
              <Input
                value={callUrl}
                onChange={e => setCallUrl(e.target.value)}
                placeholder="https://..."
                data-testid="call-url"
              />
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>Annulla</Button>
        )}
        <Button
          onClick={handleSave}
          disabled={saving || !clientId || buRatings.length === 0}
          data-testid="save-feedback-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
          {isNew ? 'Salva Feedback' : 'Aggiorna Feedback'}
        </Button>
      </div>
    </div>
  );
}

export default function CustomerFeedbackPage() {
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingFeedback, setEditingFeedback] = useState(null);
  const [activeTab, setActiveTab] = useState('list');
  const [clients, setClients] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [filterClient, setFilterClient] = useState('');
  const [filterAccount, setFilterAccount] = useState('');
  const [filterResponsible, setFilterResponsible] = useState('');
  const [filterPm, setFilterPm] = useState([]);
  const { toast } = useToast();

  useEffect(() => {
    loadFeedbacks();
    Promise.all([
      getClients().catch(() => ({ data: [] })),
      getUsers(null, true).catch(() => ({ data: [] })),
    ]).then(([c, u]) => {
      setClients(Array.isArray(c.data) ? c.data : []);
      setAllUsers(Array.isArray(u.data) ? u.data : []);
    });
  }, []);

  // Reload feedbacks when PM filter changes (server-side filtering)
  useEffect(() => {
    loadFeedbacks(filterPm);
  }, [filterPm]);

  const loadFeedbacks = async (pmIds = []) => {
    try {
      const params = { limit: 100 };
      if (pmIds.length > 0) {
        // Expand selected PM IDs to include all duplicate user IDs for the same person
        const expandedIds = new Set();
        for (const id of pmIds) {
          for (const eid of (pmNameToIds[id] || [id])) expandedIds.add(eid);
        }
        params.pm_id = [...expandedIds].join(',');
      }
      const res = await getCustomerFeedbacks(params);
      setFeedbacks(res.data.data || []);
    } catch {
      toast({ title: 'Errore caricamento feedback', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const handleCreate = async (data) => {
    await createCustomerFeedback(data);
    toast({ title: 'Feedback salvato' });
    setShowForm(false);
    loadFeedbacks();
  };

  const handleUpdate = async (data) => {
    await updateCustomerFeedback(editingFeedback.id, data);
    toast({ title: 'Feedback aggiornato' });
    setEditingFeedback(null);
    loadFeedbacks();
  };

  const handleDelete = async (id) => {
    if (!confirm('Eliminare questo feedback?')) return;
    try {
      await deleteCustomerFeedback(id);
      toast({ title: 'Feedback eliminato' });
      loadFeedbacks();
    } catch (error) {
      toast({ title: 'Errore', description: error.response?.data?.detail || 'Impossibile eliminare', variant: 'destructive' });
    }
  };

  const getRatingBadge = (val) => {
    if (!val) return null;
    if (val === 'positivo') return <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" title="Positivo" />;
    if (val === 'neutro') return <div className="w-2.5 h-2.5 rounded-full bg-amber-400" title="Neutro" />;
    return <div className="w-2.5 h-2.5 rounded-full bg-red-500" title="Negativo" />;
  };

  const getNPSColor = (score) => {
    if (!score) return 'text-gray-400';
    if (score <= 6) return 'text-red-500';
    if (score <= 8) return 'text-amber-500';
    return 'text-emerald-500';
  };

  const accountUsers = useMemo(() => allUsers.filter(u => {
    const roles = (u.roles && u.roles.length > 0) ? u.roles : (u.role ? [u.role] : []);
    return roles.includes('account') || roles.includes('admin');
  }), [allUsers]);

  // Build PM list with dedup by name, but track all IDs per name
  const { pmUsers, pmNameToIds } = useMemo(() => {
    const pms = allUsers.filter(u => {
      const roles = (u.roles && u.roles.length > 0) ? u.roles : (u.role ? [u.role] : []);
      return roles.includes('project_manager') || roles.includes('supervisor');
    });
    // Group by name to merge duplicate users
    const nameMap = {};
    for (const u of pms) {
      const nameKey = (u.name || '').toLowerCase().trim();
      if (!nameMap[nameKey]) nameMap[nameKey] = { user: u, ids: [] };
      if (!nameMap[nameKey].ids.includes(u.id)) nameMap[nameKey].ids.push(u.id);
    }
    return {
      pmUsers: Object.values(nameMap).map(v => v.user),
      pmNameToIds: Object.fromEntries(
        Object.values(nameMap).map(v => [v.user.id, v.ids])
      ),
    };
  }, [allUsers]);

  const filteredFeedbacks = useMemo(() => {
    return feedbacks.filter(fb => {
      if (filterClient && fb.client_id !== filterClient) return false;
      if (filterAccount && fb.account_manager_id !== filterAccount) return false;
      if (filterResponsible && fb.responsible_user_id !== filterResponsible) return false;
      // PM filter is now server-side, no client-side check needed
      return true;
    });
  }, [feedbacks, filterClient, filterAccount, filterResponsible]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  // Show create form
  if (showForm) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setShowForm(false)} className="text-gray-500">
            <ChevronRight className="w-4 h-4 rotate-180 mr-1" /> Indietro
          </Button>
          <h1 className="text-lg font-semibold text-gray-800">Nuovo Feedback</h1>
        </div>
        <Card>
          <CardContent className="pt-6">
            <FeedbackForm feedback={null} onSave={handleCreate} onCancel={() => setShowForm(false)} isNew={true} />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show edit form
  if (editingFeedback) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setEditingFeedback(null)} className="text-gray-500">
            <ChevronRight className="w-4 h-4 rotate-180 mr-1" /> Indietro
          </Button>
          <h1 className="text-lg font-semibold text-gray-800">Modifica Feedback</h1>
        </div>
        <Card>
          <CardContent className="pt-6">
            <FeedbackForm feedback={editingFeedback} onSave={handleUpdate} onCancel={() => setEditingFeedback(null)} isNew={false} />
          </CardContent>
        </Card>
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-orange-600" />
          <h1 className="text-lg font-semibold text-gray-800">Customer Feedback</h1>
          {activeTab === 'list' && <Badge variant="secondary" className="text-xs">{filteredFeedbacks.length}</Badge>}
        </div>
        <div className="flex items-center gap-2">
          {/* Tab switcher */}
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5" data-testid="feedback-tab-switcher">
            <button
              onClick={() => setActiveTab('list')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                activeTab === 'list' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="tab-list"
            >
              <List className="w-3.5 h-3.5" /> Lista
            </button>
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                activeTab === 'dashboard' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="tab-dashboard"
            >
              <BarChart3 className="w-3.5 h-3.5" /> Dashboard
            </button>
          </div>
          {activeTab === 'list' && hasRole(['admin', 'account']) && (
            <Button onClick={() => setShowForm(true)} data-testid="new-feedback-btn">
              <Plus className="w-4 h-4 mr-1" /> Nuovo Feedback
            </Button>
          )}
        </div>
      </div>

      {activeTab === 'dashboard' ? (
        <FeedbackDashboard />
      ) : (
        <>
          {/* List filters */}
          <Card>
            <CardContent className="py-3 px-4">
              <div className="flex flex-wrap items-end gap-3">
                <SearchableListFilter label="Cliente" value={filterClient} onChange={setFilterClient}
                  options={clients.map(c => ({ value: c.id, label: c.company_name }))} testId="list-filter-client" />
                <ListFilterSelect label="Account Manager" value={filterAccount} onChange={setFilterAccount}
                  options={accountUsers.map(u => ({ value: u.id, label: u.name }))} testId="list-filter-account" />
                <MultiSelectFilter
                  label="Project Manager"
                  selected={filterPm}
                  onChange={setFilterPm}
                  options={pmUsers.map(u => ({ value: u.id, label: u.name }))}
                  testId="list-filter-pm"
                />
                <ListFilterSelect label="Responsabile" value={filterResponsible} onChange={setFilterResponsible}
                  options={allUsers.map(u => ({ value: u.id, label: u.name }))} testId="list-filter-responsible" />
                {(filterClient || filterAccount || filterResponsible || filterPm.length > 0) && (
                  <Button variant="ghost" size="sm" className="h-8 text-xs text-gray-500"
                    onClick={() => { setFilterClient(''); setFilterAccount(''); setFilterResponsible(''); setFilterPm([]); }}
                    data-testid="list-clear-filters">
                    Reset
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {filteredFeedbacks.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="font-medium text-gray-600 mb-1">Nessun feedback registrato</h3>
            <p className="text-sm text-gray-400 mb-4">Registra il primo feedback da una call con un cliente</p>
            {hasRole(['admin', 'account']) && (
              <Button onClick={() => setShowForm(true)} variant="outline">
                <Plus className="w-4 h-4 mr-1" /> Aggiungi Feedback
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filteredFeedbacks.map(fb => (
            <Card key={fb.id} className="hover:shadow-sm transition-shadow cursor-pointer" onClick={() => setEditingFeedback(fb)} data-testid={`feedback-card-${fb.id}`}>
              <CardContent className="py-3 px-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="shrink-0">
                      <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                        <MessageSquare className="w-5 h-5 text-orange-600" />
                      </div>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-800">{fb.client_name || 'Cliente'}</span>
                        <span className="text-xs text-gray-400">
                          {fb.feedback_date ? format(parseISO(fb.feedback_date), 'dd MMM yyyy', { locale: it }) : ''}
                        </span>
                        {fb.responsible_user_name && (
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <User className="w-3 h-3" /> {fb.responsible_user_name}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        {/* BU ratings summary */}
                        {(fb.bu_ratings || []).map(r => (
                          <div key={r.business_unit_id} className="flex items-center gap-1 text-xs text-gray-500">
                            <span className="font-medium">{r.business_unit_name}:</span>
                            <div className="flex gap-0.5">
                              {getRatingBadge(r.processi)}
                              {getRatingBadge(r.operativi)}
                              {getRatingBadge(r.pm)}
                              {getRatingBadge(r.risultati)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {fb.nps_score && (
                      <div className={`text-lg font-bold ${getNPSColor(fb.nps_score)}`} data-testid="nps-display">
                        {fb.nps_score}
                      </div>
                    )}
                    {fb.call_recording_url && (
                      <Link2 className="w-4 h-4 text-blue-400" title="Call registrata" />
                    )}
                    {(hasRole(['admin']) || fb.created_by === user?.id) && (
                      <Button
                        variant="ghost" size="sm" className="h-7 w-7 p-0"
                        onClick={(e) => { e.stopPropagation(); handleDelete(fb.id); }}
                        data-testid="delete-feedback-btn"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-400" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
        </>
      )}
    </div>
  );
}

function ListFilterSelect({ label, value, onChange, options, testId }) {
  return (
    <div className="space-y-1 min-w-[150px]">
      <Label className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">{label}</Label>
      <Select value={value || 'all'} onValueChange={v => onChange(v === 'all' ? '' : v)}>
        <SelectTrigger className="h-8 text-xs" data-testid={testId}>
          <SelectValue placeholder="Tutti" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tutti</SelectItem>
          {options.map(o => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function SearchableListFilter({ label, value, onChange, options, testId }) {
  const [open, setOpen] = useState(false);
  const selected = options.find(o => o.value === value);
  return (
    <div className="space-y-1 min-w-[150px]">
      <Label className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="h-8 text-xs w-full justify-between font-normal px-3" data-testid={testId}>
            <span className="truncate">{selected ? selected.label : 'Tutti'}</span>
            <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[220px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Cerca..." className="h-8 text-xs" />
            <CommandList>
              <CommandEmpty>Nessun risultato</CommandEmpty>
              <CommandGroup>
                <CommandItem value="__all__" onSelect={() => { onChange(''); setOpen(false); }}>
                  <Check className={`mr-2 h-3 w-3 ${!value ? 'opacity-100' : 'opacity-0'}`} />
                  Tutti
                </CommandItem>
                {options.map(o => (
                  <CommandItem key={o.value} value={o.label} onSelect={() => { onChange(o.value); setOpen(false); }}>
                    <Check className={`mr-2 h-3 w-3 ${value === o.value ? 'opacity-100' : 'opacity-0'}`} />
                    {o.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function MultiSelectFilter({ label, selected, onChange, options, testId }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    if (!search) return options;
    const s = search.toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(s));
  }, [options, search]);

  const toggle = (val) => {
    onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  };

  const displayText = selected.length === 0
    ? 'Tutti'
    : selected.length === 1
      ? (options.find(o => o.value === selected[0])?.label || '1 selezionato')
      : `${selected.length} selezionati`;

  return (
    <div className="space-y-1 min-w-[150px]">
      <Label className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="h-8 text-xs w-full justify-between font-normal px-3"
            data-testid={testId}
          >
            <span className="truncate">{displayText}</span>
            <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[220px] p-0" align="start">
          <div className="p-2 border-b">
            <div className="flex items-center gap-1.5 px-1">
              <Search className="w-3.5 h-3.5 text-gray-400" />
              <input
                className="w-full text-xs bg-transparent outline-none placeholder:text-gray-400"
                placeholder="Cerca..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                data-testid={`${testId}-search`}
              />
            </div>
          </div>
          <div className="max-h-[200px] overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <div className="text-xs text-gray-400 py-2 text-center">Nessun risultato</div>
            ) : filtered.map(o => (
              <button
                key={o.value}
                onClick={() => toggle(o.value)}
                className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded hover:bg-gray-100 text-left"
                data-testid={`${testId}-option-${o.value}`}
              >
                <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${
                  selected.includes(o.value) ? 'bg-orange-500 border-orange-500' : 'border-gray-300'
                }`}>
                  {selected.includes(o.value) && <Check className="w-2.5 h-2.5 text-white" />}
                </div>
                <span className="truncate">{o.label}</span>
              </button>
            ))}
          </div>
          {selected.length > 0 && (
            <div className="p-1.5 border-t">
              <button
                onClick={() => onChange([])}
                className="w-full text-xs text-gray-500 hover:text-gray-700 py-1"
                data-testid={`${testId}-clear`}
              >
                Deseleziona tutti
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
